import { useEffect, useRef, useState } from 'react';
import { STATE_ABBR } from '@/lib/usStates';

export interface ParsedAddress {
  street: string;
  city: string;
  state: string;
  zip: string;
  country: string;
}

interface AddressAutocompleteProps {
  value: string;
  /** Fired on each keystroke AND when a suggestion is picked (street line only). */
  onChange: (street: string) => void;
  /** Fired when the user picks a suggestion — fill city/state/zip/country. */
  onSelect: (address: ParsedAddress) => void;
  className?: string;
  placeholder?: string;
  id?: string;
  /** Comma-separated ISO country codes to bias results. Default US + Canada. */
  countries?: string;
}

interface NominatimResult {
  display_name: string;
  address: Record<string, string>;
}

// Full state/province name → 2-letter code (the form's State field is 2 chars).


function parseAddress(a: Record<string, string>): ParsedAddress {
  const street = [a.house_number, a.road || a.pedestrian || a.footway]
    .filter(Boolean)
    .join(' ')
    .trim();
  const stateFull = a.state || a.province || '';
  return {
    street,
    city: a.city || a.town || a.village || a.hamlet || a.suburb || a.municipality || a.county || '',
    state: STATE_ABBR[stateFull] || (stateFull.length === 2 ? stateFull.toUpperCase() : stateFull),
    zip: a.postcode || '',
    country: a.country || '',
  };
}

/**
 * Street-address input with autocomplete, from Google Places when a key is
 * configured and OpenStreetMap / Nominatim when one is not.
 *
 * Google is the better source for the US addresses a courier has to deliver to:
 * Nominatim's public endpoint asks for ~1 req/s, misses newer subdivisions, and
 * frequently returns no house number for a valid street — which lands a
 * half-filled address in a form whose whole point is that the package arrives.
 * This component already carried a note to "re-enable Google Places"; the key
 * is live (deploy.yml passes VITE_GOOGLE_MAPS_KEY, and the eForm address field
 * has been using Places on it), so this switches over and keeps Nominatim as
 * the fallback for local dev and any host without the key.
 *
 * Both paths produce the SAME ParsedAddress, so callers cannot tell which ran —
 * the input stays fully controlled and always typeable, and if a lookup fails
 * it degrades to a plain text field rather than blocking the order.
 */
const GEOCODE_URL =
  (import.meta.env.VITE_GEOCODE_URL as string) || 'https://nominatim.openstreetmap.org/search';

/** Read per call, not once at import: a module-level capture cannot be stubbed,
 *  and this is the delivery-address field for two integrations — it needs tests. */
const googleKey = (): string => (import.meta.env.VITE_GOOGLE_MAPS_KEY as string) || '';

/** One Places suggestion, normalised away from Google's nested shape. */
interface PlacesSuggestion {
  mainText: string;
  secondaryText: string;
  prediction: { toPlace: () => GooglePlace };
}

interface GooglePlace {
  fetchFields: (opts: { fields: string[] }) => Promise<unknown>;
  addressComponents?: Array<{ types?: string[]; longText?: string; shortText?: string }>;
}

/** Load the Maps JS + Places library once per document. */
function loadGooglePlaces(): void {
  const key = googleKey();
  if (!key) return;
  if (document.getElementById('google-maps-places-script')) return;
  const w = window as unknown as { google?: { maps?: { places?: unknown } } };
  if (w.google?.maps?.places) return;

  const script = document.createElement('script');
  script.id = 'google-maps-places-script';
  script.src = `https://maps.googleapis.com/maps/api/js?key=${key}&libraries=places`;
  script.async = true;
  document.head.appendChild(script);
}

/** Google's address components, flattened into the shape every caller expects. */
function parseGooglePlace(place: GooglePlace): ParsedAddress {
  let number = '';
  let route = '';
  const out: ParsedAddress = { street: '', city: '', state: '', zip: '', country: '' };

  for (const comp of place.addressComponents ?? []) {
    const types = comp.types ?? [];
    if (types.includes('street_number')) number = comp.longText ?? '';
    else if (types.includes('route')) route = comp.longText ?? '';
    else if (types.includes('locality') || types.includes('sublocality_level_1')) out.city = comp.longText ?? '';
    else if (types.includes('administrative_area_level_1')) out.state = comp.shortText ?? '';
    else if (types.includes('postal_code')) out.zip = comp.longText ?? '';
    else if (types.includes('country')) out.country = comp.shortText ?? '';
  }
  out.street = [number, route].filter(Boolean).join(' ').trim();

  return out;
}

export function AddressAutocomplete({
  value,
  onChange,
  onSelect,
  className,
  placeholder,
  id,
  countries = 'us,ca',
}: AddressAutocompleteProps) {
  const [results, setResults] = useState<NominatimResult[]>([]);
  const [places, setPlaces] = useState<PlacesSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  /** A ref, not state: the debounced search closes over this at call time, and
   *  a value captured one render too early sends the FIRST lookup after mount
   *  to Nominatim even though Places is loaded. */
  const googleReadyRef = useRef(false);
  const boxRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastQuery = useRef('');
  /** Groups a typing session into one billed Places lookup. */
  const sessionTokenRef = useRef<unknown>(null);

  // Load Places and wait for it. Until it is ready, Nominatim answers — so the
  // field is never dead while a script downloads.
  useEffect(() => {
    if (!googleKey()) return;
    loadGooglePlaces();

    const tick = window.setInterval(() => {
      const w = window as unknown as { google?: { maps?: { places?: unknown } } };
      if (w.google?.maps?.places) {
        googleReadyRef.current = true;
        window.clearInterval(tick);
      }
    }, 150);
    // Give up quietly after 10s and stay on Nominatim.
    const stop = window.setTimeout(() => window.clearInterval(tick), 10_000);

    return () => { window.clearInterval(tick); window.clearTimeout(stop); };
  }, []);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const searchGoogle = async (q: string) => {
    const g = (window as unknown as { google: any }).google;
    if (!sessionTokenRef.current) sessionTokenRef.current = new g.maps.places.AutocompleteSessionToken();

    const { suggestions } = await g.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
      input: q,
      includedPrimaryTypes: ['street_address', 'subpremise', 'premise'],
      includedRegionCodes: countries.split(',').map((c) => c.trim()).filter(Boolean),
      sessionToken: sessionTokenRef.current,
    });

    return (suggestions ?? [])
      .filter((s: any) => s.placePrediction)
      .map((s: any): PlacesSuggestion => ({
        mainText: s.placePrediction.mainText?.text || s.placePrediction.text?.text || '',
        secondaryText: s.placePrediction.secondaryText?.text || '',
        prediction: s.placePrediction,
      }));
  };

  const search = (q: string) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (q.trim().length < 3) {
      setResults([]);
      setPlaces([]);
      setOpen(false);
      return;
    }
    timerRef.current = setTimeout(async () => {
      const w = window as unknown as { google?: { maps?: { places?: unknown } } };
      if (googleReadyRef.current || w.google?.maps?.places) {
        lastQuery.current = q;
        setLoading(true);
        try {
          const mapped = await searchGoogle(q);
          if (lastQuery.current === q) {
            setPlaces(mapped);
            setResults([]);
            setOpen(true);
          }
        } catch {
          /* Places down or over quota → the field stays typeable */
          setPlaces([]);
        } finally {
          setLoading(false);
        }

        return;
      }

      lastQuery.current = q;
      setLoading(true);
      try {
        const url = `${GEOCODE_URL}?format=jsonv2&addressdetails=1&limit=6&countrycodes=${countries}&q=${encodeURIComponent(q)}`;
        const res = await fetch(url, { headers: { Accept: 'application/json' } });
        const data = (await res.json()) as NominatimResult[];
        if (lastQuery.current === q) {
          setResults(Array.isArray(data) ? data : []);
          setOpen(true);
        }
      } catch {
        /* network/geocoder down → stay a plain input */
      } finally {
        setLoading(false);
      }
    }, 400);
  };

  const pickPlace = async (sug: PlacesSuggestion) => {
    setOpen(false);
    setPlaces([]);
    onChange(sug.mainText);
    try {
      const place = sug.prediction.toPlace();
      await place.fetchFields({ fields: ['addressComponents'] });
      // The token's session ends with the pick; the next search starts a new one.
      sessionTokenRef.current = null;
      const parsed = parseGooglePlace(place);
      if (parsed.street) onChange(parsed.street);
      onSelect(parsed);
    } catch {
      /* details lookup failed → keep what was typed rather than clearing it */
    }
  };

  const pick = (r: NominatimResult) => {
    const parsed = parseAddress(r.address || {});
    if (parsed.street) onChange(parsed.street);
    onSelect(parsed);
    setOpen(false);
    setResults([]);
  };

  return (
    <div ref={boxRef} className="relative">
      <input
        id={id}
        className={className}
        value={value}
        placeholder={placeholder ?? 'Start typing an address…'}
        autoComplete="off"
        onChange={(e) => {
          onChange(e.target.value);
          search(e.target.value);
        }}
        onFocus={() => {
          if (results.length) setOpen(true);
        }}
      />
      {open && (loading || places.length > 0 || results.length > 0) && (
        <ul className="absolute left-0 right-0 z-[100000] mt-1 max-h-64 overflow-auto rounded-md border border-slate-200 bg-white text-sm shadow-lg">
          {loading && <li className="px-3 py-2 text-slate-400">Searching…</li>}
          {places.map((sug, i) => (
            <li
              key={`g-${i}`}
              className="cursor-pointer px-3 py-2 hover:bg-slate-50"
              onMouseDown={(e) => { e.preventDefault(); void pickPlace(sug); }}
            >
              <div className="font-medium text-slate-800">{sug.mainText}</div>
              <div className="truncate text-xs text-slate-500">{sug.secondaryText}</div>
            </li>
          ))}
          {results.map((r, i) => {
            const p = parseAddress(r.address || {});
            return (
              <li
                key={i}
                className="cursor-pointer px-3 py-2 hover:bg-slate-50"
                onMouseDown={(e) => {
                  e.preventDefault();
                  pick(r);
                }}
              >
                <div className="font-medium text-slate-800">
                  {p.street || r.display_name.split(',')[0]}
                </div>
                <div className="truncate text-xs text-slate-500">{r.display_name}</div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
